import React, { useCallback, useState } from "react";

import {
  faExclamationTriangle,
  faPlus,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";
import { faCrosshairs } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";

import { deleteSaleLotScansAction } from "actions";

import { SubtleBadge } from "components/Badge";
import { IconTextButton } from "components/Button";
import {
  ConfirmDialog,
  MESSAGE_CANT_BE_UNDONE,
} from "components/ConfirmDialog";
import { CollapseLabel, FormCollapse } from "components/Form";
import { Input } from "components/Form/FormikControls";
import { Tooltip as InfoTooltip } from "components/Form/FormikControls/Tooltip";
import { Column, Row } from "components/Layout";
import { CurrentSaleReadOnly } from "components/ReadOnly";
import { ResponsiveText } from "components/ResponsiveText";

import { PenTypes } from "constants/auctionPens";
import { TransferStatus } from "constants/nlis";
import { SaleyardPermissions } from "constants/permissions";
import { ScanLabelArchetype } from "constants/scanLabels";
import { colors } from "constants/theme";

import { useHasSaleyardPermission } from "hooks";

import { getScanRoute, openScanModal } from "lib/navigation";
import { getApiSale } from "lib/sale";
import {
  getCombinedLotNumber,
  getSaleLotScannedStatus,
  getScanStatusThreshold,
} from "lib/saleLot";
import { getScanPercentDisplayValue, getScanStatusColour } from "lib/scans";

import {
  getCurrentSale,
  getCurrentSaleyard,
  getEidsBySaleLotId,
  getSaleLotById,
  getScanLabelArchetypeCountsBySaleLotId,
  getScansBySaleLotId,
} from "selectors";

interface PercentageProps {
  color?: string;
}

const Percentage = styled.span<PercentageProps>`
  font-weight: 500;
  color: ${({ theme, color }) => theme.colors[color]};
`;

function useSaleLotScanStatus(
  saleLotId: string,
  quantity: number,
  quantityProgeny: number,
) {
  const saleLot = useSelector(getSaleLotById(saleLotId)) || {};
  const speciesId = useSelector(state => getCurrentSale(state).species_id);
  const scannedCount = useSelector(
    state => (getEidsBySaleLotId(saleLotId)(state) || []).length,
  );
  const saleyard = useSelector(getCurrentSaleyard);

  const scans = useSelector(getScansBySaleLotId(saleLotId)) || [];

  const expectedScanCount = quantity + quantityProgeny;
  const scannedStatus = getSaleLotScannedStatus(
    saleLot,
    scannedCount,
    getScanStatusThreshold(speciesId, saleyard),
  );
  const scannedPercentDisplayValue = getScanPercentDisplayValue(
    expectedScanCount,
    scannedCount,
  );

  const hasNlisTransferredScans = scans.some(
    scan =>
      scan.nlis_sell_status !== TransferStatus.UNSUBMITTED ||
      scan.nlis_take_status !== TransferStatus.UNSUBMITTED,
  );

  return {
    saleLot,
    scannedCount,
    expectedScanCount,
    scannedStatus,
    scannedPercentDisplayValue,
    hasNlisTransferredScans,
  };
}

interface ScanningDetailsProps {
  saleLotId: string;
  quantity: number;
  quantityProgeny: number;
  readOnly?: boolean;
}

export const ScanningDetails = ({
  saleLotId,
  quantity,
  quantityProgeny,
  readOnly,
}: ScanningDetailsProps): React.JSX.Element => {
  const [isRescanDialogOpen, setIsRescanDialogOpen] = useState(false);
  const dispatch = useDispatch();

  const {
    saleLot,
    scannedCount,
    expectedScanCount,
    scannedStatus,
    scannedPercentDisplayValue,
    hasNlisTransferredScans,
  } = useSaleLotScanStatus(saleLotId, quantity, quantityProgeny);

  const lotNumber = getCombinedLotNumber(saleLot);

  const hasScanningStarted = scannedCount > 0;

  const { pathname, search, hash } = window.location;
  const returnTo = `${pathname}${search}${hash}`;

  const scanUrl = `#${getScanRoute(
    null,
    saleLot?.id,
    true,
    null,
    returnTo,
    null,
    null,
    null,
    PenTypes.SELLING,
  )}`;

  const openRescanDialog = useCallback(
    () => setIsRescanDialogOpen(true),
    [setIsRescanDialogOpen],
  );

  const closeRescanDialog = useCallback(
    () => setIsRescanDialogOpen(false),
    [setIsRescanDialogOpen],
  );

  const onRescan = useCallback(() => {
    const sale = getApiSale();
    dispatch(deleteSaleLotScansAction(saleLotId, sale));
    openScanModal(null, saleLotId, false, null, PenTypes.SELLING, returnTo);
  }, [dispatch, returnTo, saleLotId]);

  const lotNumberString = lotNumber
    ? `Sale Lot #${lotNumber}`
    : "this Sale Lot";

  return (
    <>
      <Row justifyBetween style={{ padding: "0 12px 12px 12px" }} full>
        <Column>
          <CollapseLabel padding>
            <ResponsiveText
              mobile="Scanned Hd"
              tablet="Scanned Hd"
              desktop="Scanned Head Count"
            />
          </CollapseLabel>
          {hasScanningStarted ? (
            <div>{`${scannedCount}/${expectedScanCount}`}</div>
          ) : (
            <CollapseLabel>-</CollapseLabel>
          )}
        </Column>
        <Column>
          <CollapseLabel padding>
            <ResponsiveText
              mobile="%"
              tablet="%"
              desktop="Percentage Scanned"
            />
          </CollapseLabel>
          {hasScanningStarted ? (
            <Percentage color={getScanStatusColour(scannedStatus)}>
              {scannedPercentDisplayValue}
            </Percentage>
          ) : (
            <CollapseLabel>-</CollapseLabel>
          )}
        </Column>
        <CurrentSaleReadOnly>
          {hasScanningStarted ? (
            <Row alignEnd justifyEnd>
              <IconTextButton
                dataTour="addToScan"
                icon={faPlus}
                as={Link}
                to={scanUrl}
                disabled={readOnly}
              >
                Add to Scan
              </IconTextButton>
              <Tooltip
                title={
                  hasNlisTransferredScans
                    ? "Cannot re-scan: EIDs have already been transferred on the NLIS"
                    : ""
                }
              >
                <span>
                  <IconTextButton
                    dataTour="rescan"
                    icon={faRedo}
                    onClick={openRescanDialog}
                    disabled={readOnly || hasNlisTransferredScans}
                  >
                    Re-scan
                  </IconTextButton>
                </span>
              </Tooltip>
            </Row>
          ) : (
            <Row data-tour="scanSaleLot" alignEnd justifyEnd>
              <IconTextButton
                icon={faCrosshairs}
                as={Link}
                to={scanUrl}
                disabled={readOnly}
              >
                Scan Sale Lot
              </IconTextButton>
            </Row>
          )}
        </CurrentSaleReadOnly>
      </Row>

      <ConfirmDialog
        title="Confirm you want to Clear Scans"
        isOpen={isRescanDialogOpen}
        onCancel={closeRescanDialog}
        message={
          scannedCount > 0 ? (
            <div>
              <FontAwesomeIcon
                color={colors.warningOrange}
                icon={faExclamationTriangle}
              />{" "}
              Are you sure you want to clear all scans from {lotNumberString}?{" "}
              {MESSAGE_CANT_BE_UNDONE}{" "}
            </div>
          ) : (
            MESSAGE_CANT_BE_UNDONE
          )
        }
        buttonMessage="Clear Scans"
        onDelete={onRescan}
      />
    </>
  );
};

interface TagsUsedOrScanLabelCountsProps {
  saleLotId: string;
  quantityTagsUsedFieldName: string;
  readOnly?: boolean;
}

const ScanLabelCountFields: [ScanLabelArchetype, string, string][] = [
  [
    ScanLabelArchetype.DECEASED,
    "Deceased Count",
    "How many animals in this Sale Lot were labelled with 'Deceased'.",
  ],
  [
    ScanLabelArchetype.NON_READER,
    "Non-Reader Count",
    "How many animals in this Sale Lot were labelled with 'Non-Reader'.",
  ],
  [
    ScanLabelArchetype.NO_TAG,
    "No Tag Count",
    "How many animals in this Sale Lot were labelled with 'No Tag'.",
  ],
];

export const TagsUsedOrScanLabelCounts = ({
  saleLotId,
  quantityTagsUsedFieldName,
  readOnly,
}: TagsUsedOrScanLabelCountsProps): React.JSX.Element => {
  const canAddScanLabels = useHasSaleyardPermission(
    SaleyardPermissions.canAddScanLabels,
  );
  const archetypeCounts = useSelector(
    getScanLabelArchetypeCountsBySaleLotId(saleLotId),
  );

  if (canAddScanLabels) {
    return (
      <Row justifyBetween paddingHorizontal={2} full>
        {ScanLabelCountFields.map(([archetype, label, tooltip]) => (
          <Column key={archetype}>
            <Row alignCenter>
              <CollapseLabel padding>{label}</CollapseLabel>
              <InfoTooltip title={tooltip} />
            </Row>
            <div>{archetypeCounts?.[archetype] ?? 0}</div>
          </Column>
        ))}
      </Row>
    );
  }

  return (
    <Grid container>
      <Grid item xs={6}>
        <Input
          label="Tags Used Quantity"
          name={quantityTagsUsedFieldName}
          tooltip="The number of tags required (including replacement and new tags) for this Sale Lot. "
          type="number"
          min={0}
          disabled={readOnly}
        />
      </Grid>
    </Grid>
  );
};

interface ScanningCollapseProps {
  isOpen?: boolean;
  onToggle?: () => void;
  saleLotId: string;
  quantity?: number;
  quantityProgeny?: number;
  readOnly?: boolean;
}

function ScanningCollapse({
  isOpen,
  onToggle,
  saleLotId,
  quantity = 0,
  quantityProgeny = 0,
  readOnly,
}: ScanningCollapseProps): React.JSX.Element {
  const {
    scannedCount,
    expectedScanCount,
    scannedStatus,
    scannedPercentDisplayValue,
  } = useSaleLotScanStatus(saleLotId, quantity, quantityProgeny);

  const header = (
    <Row>
      <SubtleBadge>Scanning</SubtleBadge>
      <Row alignCenter flexWrap>
        <CollapseLabel>{`${scannedCount}/${expectedScanCount} scanned`}</CollapseLabel>
        <Percentage color={getScanStatusColour(scannedStatus)}>
          {scannedPercentDisplayValue}
        </Percentage>
      </Row>
    </Row>
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={header}
      id="collapse-scanning"
      dataTour={isOpen ? "hideScanning" : "showScanning"}
    >
      <ScanningDetails
        saleLotId={saleLotId}
        quantity={quantity}
        quantityProgeny={quantityProgeny}
        readOnly={readOnly}
      />
      <TagsUsedOrScanLabelCounts
        saleLotId={saleLotId}
        quantityTagsUsedFieldName="quantityTagsUsed"
        readOnly={readOnly}
      />
    </FormCollapse>
  );
}

export default ScanningCollapse;
